import React, { useEffect, useState } from "react";
import CloseIcon from "../../assets/CloseIcon.svg";
import { CustomTable } from "../../components/Table";
import { createColumnHelper } from "@tanstack/react-table";
import { Tooltip } from "react-tooltip";
import Checkbox from "../../components/CheckBox";
import InfoIcon from "../../assets/info.svg";
import CustomDropDown from "../../components/DropDown";
import ServiceButton from "../../components/Button/ServiceButton";
import InfoIconDisabled from "../../assets/info-circle-disabled.svg";
import {
  GET_COMMUNICATION_RATE_CARD,
  UPDATE_COMMUNICATION_RATE_CARD,
} from "../../utils/ApiUrls";
import { POST } from "../../utils/webService";
import toast from "react-hot-toast";

interface IDeltaOnBlazeProps {
  setOpenRightModalForDelta: any;
}

// Eye Icon Component
const EyeIcon = ({ disabled = false }: { disabled?: boolean }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke={disabled ? "#E8E8E8" : "#1C1C1C"}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

// Back Arrow Icon
const BackArrowIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#1C1C1C"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M19 12H5M12 19l-7-7 7-7" />
  </svg>
);

// WhatsApp Icon
const WhatsAppIcon = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
    <rect width="40" height="40" rx="20" fill="#E8F5E9" />
    <path
      d="M20 10C14.5 10 10 14.5 10 20C10 21.9 10.5 23.7 11.4 25.2L10 30L14.9 28.6C16.4 29.4 18.1 29.9 20 29.9C25.5 29.9 30 25.4 30 19.9C30 14.5 25.5 10 20 10ZM25.9 24.6C25.6 25.4 24.4 26.1 23.5 26.3C22.9 26.4 22.1 26.5 19.3 25.3C15.8 23.9 13.5 20.3 13.3 20.1C13.1 19.8 11.7 18 11.7 16.1C11.7 14.2 12.7 13.3 13.1 12.9C13.4 12.5 13.9 12.4 14.2 12.4C14.3 12.4 14.5 12.4 14.6 12.4C14.9 12.4 15.1 12.5 15.4 13.1C15.7 13.8 16.4 15.7 16.5 15.9C16.6 16.1 16.6 16.3 16.5 16.5C16.4 16.7 16.3 16.9 16.1 17.1C15.9 17.3 15.7 17.6 15.6 17.7C15.4 17.9 15.2 18.1 15.4 18.5C15.6 18.8 16.4 20.1 17.5 21.1C18.9 22.3 20.1 22.7 20.5 22.9C20.8 23 21.1 23 21.3 22.8C21.5 22.5 22.1 21.8 22.4 21.5C22.6 21.2 22.9 21.1 23.2 21.2C23.5 21.3 25.4 22.2 25.7 22.4C26.1 22.6 26.3 22.7 26.4 22.8C26.5 23.1 26.5 23.8 25.9 24.6Z"
      fill="#25D366"
    />
  </svg>
);

// SMS Icon
const SMSIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <rect width="24" height="24" rx="4" fill="#E3F2FD" />
    <path
      d="M4 6C4 4.9 4.9 4 6 4H18C19.1 4 20 4.9 20 6V14C20 15.1 19.1 16 18 16H8L4 20V6Z"
      fill="#2196F3"
    />
  </svg>
);

// Order Tracking Templates Component
interface OrderTrackingTemplatesProps {
  templateName: string;
  onBack: () => void;
}

const OrderTrackingTemplates: React.FC<OrderTrackingTemplatesProps> = ({
  templateName,
  onBack,
}) => {
  const [activeTab, setActiveTab] = useState<"WhatsApp" | "SMS" | "Email">(
    "WhatsApp"
  );

  const tabs = ["WhatsApp", "SMS", "Email"] as const;

  // WhatsApp Templates Content
  const WhatsAppContent = () => (
    <div className="p-4 space-y-6 pb-16">
      {/* Order Picked Up Template */}
      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
        <div className="flex items-center gap-3 mb-3">
          <WhatsAppIcon />
          <h3 className="font-Open font-semibold text-lg text-[#1C1C1C]">
            Order Picked Up
          </h3>
        </div>
        <div className="bg-[#F9FAFB] rounded-lg p-4">
          <p className="font-Open text-sm text-[#1C1C1C] mb-2">
            Hello {"{{1}}"}
          </p>
          <p className="font-Open text-sm text-[#1C1C1C] mb-2">
            Your Order from {"{{2}}"} has been picked up.
          </p>
          <p className="font-Open text-sm text-[#1C1C1C] mb-3">
            Click on below link to track your order.
          </p>
          <p className="font-Open text-xs text-[#6B7280] mb-3">Shipyaari</p>
          <button className="w-full border border-[#3B82F6] text-[#3B82F6] rounded-lg py-2 flex items-center justify-center gap-2 hover:bg-blue-50 transition-colors">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
            Track Now
          </button>
        </div>
      </div>

      {/* Reached Destination City Template */}
      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
        <div className="flex items-center gap-3 mb-3">
          <WhatsAppIcon />
          <h3 className="font-Open font-semibold text-lg text-[#1C1C1C]">
            Reached Destination City
          </h3>
        </div>
        <div className="bg-[#F9FAFB] rounded-lg p-4">
          <p className="font-Open text-sm text-[#1C1C1C] mb-2">
            Hello {"{{1}}"}
          </p>
          <p className="font-Open text-sm text-[#1C1C1C] mb-2">
            Your Order from {"{{2}}"} has reached the Destination City.
          </p>
          <p className="font-Open text-sm text-[#1C1C1C] mb-3">
            Click on the link below to track your order.
          </p>
          <p className="font-Open text-xs text-[#6B7280] mb-3">Shipyaari</p>
          <button className="w-full border border-[#3B82F6] text-[#3B82F6] rounded-lg py-2 flex items-center justify-center gap-2 hover:bg-blue-50 transition-colors">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
            Track Now
          </button>
        </div>
      </div>

      {/* Out For Delivery Template */}
      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
        <div className="flex items-center gap-3 mb-3">
          <WhatsAppIcon />
          <h3 className="font-Open font-semibold text-lg text-[#1C1C1C]">
            Out For Delivery
          </h3>
        </div>
        <div className="bg-[#F9FAFB] rounded-lg p-4">
          <p className="font-Open text-sm text-[#1C1C1C] mb-2">
            Hello {"{{1}}"}
          </p>
          <p className="font-Open text-sm text-[#1C1C1C] mb-2">
            Your Order from {"{{2}}"} is out for delivery.
          </p>
          <p className="font-Open text-sm text-[#1C1C1C] mb-3">
            Our Delivery Executive will get in touch with you soon.
          </p>
          <p className="font-Open text-xs text-[#6B7280] mb-3">Shipyaari</p>
          <button className="w-full border border-[#3B82F6] text-[#3B82F6] rounded-lg py-2 flex items-center justify-center gap-2 hover:bg-blue-50 transition-colors">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
            Track Now
          </button>
        </div>
      </div>

      {/* Order Delivered Template */}
      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
        <div className="flex items-center gap-3 mb-3">
          <WhatsAppIcon />
          <h3 className="font-Open font-semibold text-lg text-[#1C1C1C]">
            Order Delivered
          </h3>
        </div>
        <div className="bg-[#F9FAFB] rounded-lg p-4">
          <p className="font-Open text-sm text-[#1C1C1C] mb-2">
            Hello {"{{1}}"},
          </p>
          <p className="font-Open text-sm text-[#1C1C1C] mb-2">
            Your order from {"{{2}}"} is delivered by Shipyaari.
          </p>
          <p className="font-Open text-sm text-[#1C1C1C] mb-3">
            Tell us how we did! Rate us delivery experience on the link below and share your thoughts on Shipyaari delivery experience.
          </p>
          <p className="font-Open text-xs text-[#6B7280] mb-3">Shipyaari</p>
          <button className="w-full border border-[#3B82F6] text-[#3B82F6] rounded-lg py-2 flex items-center justify-center gap-2 hover:bg-blue-50 transition-colors">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
            Rate Us
          </button>
        </div>
      </div>

      {/* NDR - Your Shipment Update Template */}
      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
        <div className="flex items-center gap-3 mb-3">
          <WhatsAppIcon />
          <h3 className="font-Open font-semibold text-lg text-[#1C1C1C]">
            Your Shipment Update!
          </h3>
        </div>
        <div className="bg-[#F9FAFB] rounded-lg p-4">
          <p className="font-Open text-sm text-[#1C1C1C] mb-2">
            Hello {"{{1}}"},
          </p>
          <p className="font-Open text-sm text-[#1C1C1C] mb-2">
            Your order {"{{2}}"} couldn't be delivered to you for this reported reason: {"{{3}}"}
          </p>
          <p className="font-Open text-sm text-[#1C1C1C] mb-3">
            Please let us know how we can assist you?
          </p>
          <p className="font-Open text-sm font-semibold text-[#1C1C1C] mb-2">
            Order details:
          </p>
          <div className="font-Open text-sm text-[#1C1C1C] mb-3 space-y-1">
            <p>🛒 {"{{4}}"}</p>
            <p>👨 Seller: {"{{5}}"}</p>
            <p>🛒 Order ID: {"{{6}}"}</p>
            <p>📅 Date: {"{{7}}"}</p>
            <p>🚚 Courier ID: {"{{8}}"}</p>
            <p>🚚 Airway Bill {"{{9}}"}</p>
          </div>
          <p className="font-Open text-xs text-[#6B7280] mb-3">Shipyaari</p>
          <div className="space-y-2">
            <button className="w-full border border-[#3B82F6] text-[#3B82F6] rounded-lg py-2 flex items-center justify-center gap-2 hover:bg-blue-50 transition-colors">
              Reattempt Delivery
            </button>
            <button className="w-full border border-[#3B82F6] text-[#3B82F6] rounded-lg py-2 flex items-center justify-center gap-2 hover:bg-blue-50 transition-colors">
              Cancel My Order
            </button>
          </div>
        </div>
      </div>

      {/* Order Confirmation Template */}
      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
        <div className="flex items-center gap-3 mb-3">
          <WhatsAppIcon />
          <h3 className="font-Open font-semibold text-lg text-[#1C1C1C]">
            Your Order Confirmation!
          </h3>
        </div>
        <div className="bg-[#F9FAFB] rounded-lg p-4">
          <p className="font-Open text-sm text-[#1C1C1C] mb-2">
            Hi {"{{1}}"} ,
          </p>
          <p className="font-Open text-sm text-[#1C1C1C] mb-3">
            Your order is packed and ready to move out!
          </p>
          <div className="font-Open text-sm text-[#1C1C1C] mb-3 space-y-2">
            <p>🛒 Order ID: {"{{2}}"}</p>
            <p>🛒 Item(s): {"{{3}}"}</p>
            <p>💰 Order amount: ₹{"{{4}}"}</p>
            <p>💲 Mode: {"{{5}}"}</p>
          </div>
          <p className="font-Open text-sm text-[#1C1C1C] mb-3">
            Please select an option to confirm your order.
          </p>
          <p className="font-Open text-xs text-[#6B7280] mb-3">from shipyaari.com</p>
          <div className="space-y-2">
            <button className="w-full border border-[#3B82F6] text-[#3B82F6] rounded-lg py-2 flex items-center justify-center gap-2 hover:bg-blue-50 transition-colors">
              I Want This Order
            </button>
            <button className="w-full border border-[#3B82F6] text-[#3B82F6] rounded-lg py-2 flex items-center justify-center gap-2 hover:bg-blue-50 transition-colors">
              Cancel My Order
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // SMS Templates Content
  const SMSContent = () => (
    <div className="p-4 space-y-4 pb-16">
      {/* Picked Up */}
      <div className="bg-white rounded-lg p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <SMSIcon />
          <h3 className="font-Open font-bold text-base text-[#1C1C1C]">
            PICKED UP
          </h3>
        </div>
        <div className="bg-[#F9FAFB] rounded-lg p-3">
          <p className="font-Open text-sm text-[#4B5563]">
            Hi! Your {"{#var#}"} order is picked up by Shipyaari. Delivery is expected by {"{#var#}"}. Track your order at {"{#var#}"}. Shipyaari
          </p>
        </div>
      </div>

      {/* Reached Destination */}
      <div className="bg-white rounded-lg p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <SMSIcon />
          <h3 className="font-Open font-bold text-base text-[#1C1C1C]">
            REACHED DESTINATION
          </h3>
        </div>
        <div className="bg-[#F9FAFB] rounded-lg p-3">
          <p className="font-Open text-sm text-[#4B5563]">
            Great news! Your {"{#var#}"} order is in town. Estimated delivery date is {"{#var#}"}. Track your order at {"{#var#}"}. Shipyaari
          </p>
        </div>
      </div>

      {/* COD OFD */}
      <div className="bg-white rounded-lg p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <SMSIcon />
          <h3 className="font-Open font-bold text-base text-[#1C1C1C]">
            COD OFD
          </h3>
        </div>
        <div className="bg-[#F9FAFB] rounded-lg p-3">
          <p className="font-Open text-sm text-[#4B5563]">
            Hi! Your {"{#var#}"} order shall be delivered today by Shipyaari. Please keep INR {"{#var#}"} cash ready. Track at: {"{#var#}"}. Shipyaari
          </p>
        </div>
      </div>

      {/* Prepaid OFD */}
      <div className="bg-white rounded-lg p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <SMSIcon />
          <h3 className="font-Open font-bold text-base text-[#1C1C1C]">
            PREPAID OFD
          </h3>
        </div>
        <div className="bg-[#F9FAFB] rounded-lg p-3">
          <p className="font-Open text-sm text-[#4B5563]">
            Hello! Your order from {"{#var#}"} is set to be delivered today by Shipyaari. Track your order at {"{#var#}"}. Shipyaari
          </p>
        </div>
      </div>

      {/* Delivered */}
      <div className="bg-white rounded-lg p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <SMSIcon />
          <h3 className="font-Open font-bold text-base text-[#1C1C1C]">
            DELIVERED
          </h3>
        </div>
        <div className="bg-[#F9FAFB] rounded-lg p-3">
          <p className="font-Open text-sm text-[#4B5563]">
            Good news! Your order from {"{#var#}"} has been successfully delivered by Shipyaari. Please contact your seller for any help required. Shipyaari
          </p>
        </div>
      </div>

      {/* NDR */}
      <div className="bg-white rounded-lg p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <SMSIcon />
          <h3 className="font-Open font-bold text-base text-[#1C1C1C]">
            NDR
          </h3>
        </div>
        <div className="bg-[#F9FAFB] rounded-lg p-3">
          <p className="font-Open text-sm text-[#4B5563]">
            We regret to inform you that your {"{#var#}"} order couldn't be delivered by Shipyaari. To schedule a re-attempt, click at: {"{#var#}"}. To cancel or reject delivery, click at: {"{#var#}"}. Please contact your seller if you need help with your order. Shipyaari
          </p>
        </div>
      </div>
    </div>
  );

  // Email Templates Content
  const EmailContent = () => (
    <div className="p-4 pb-16">
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 max-w-md mx-auto">
        {/* Email Header */}
        <div className="text-center pt-6">
          <div className="inline-block bg-[#3B82F6] text-white px-6 py-2 rounded-full font-semibold text-sm">
            Shipyaari
          </div>
        </div>

        {/* Email Banner */}
        <div className="px-4 pt-4">
          <div className="bg-[#3B82F6] text-white text-center py-3 rounded-lg font-medium">
            Your Order Has Been Picked Up
          </div>
        </div>

        {/* Status Tracker */}
        <div className="flex justify-between items-center px-6 py-6">
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-full bg-[#E8F5E9] flex items-center justify-center mb-2">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#22C55E"
                strokeWidth="2"
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </div>
            <span className="text-xs text-[#1C1C1C]">Picked</span>
          </div>
          <div className="flex-1 h-0.5 bg-[#E5E7EB] mx-2" />
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-full bg-[#3B82F6] flex items-center justify-center mb-2">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="2"
              >
                <rect x="1" y="3" width="15" height="13" />
                <path d="M16 8h4l3 3v5h-7V8z" />
                <circle cx="5.5" cy="18.5" r="2.5" />
                <circle cx="18.5" cy="18.5" r="2.5" />
              </svg>
            </div>
            <span className="text-xs text-[#1C1C1C]">In Transit</span>
          </div>
          <div className="flex-1 h-0.5 bg-[#E5E7EB] mx-2" />
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-full bg-[#FEF3C7] flex items-center justify-center mb-2">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#F59E0B"
                strokeWidth="2"
              >
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
            </div>
            <span className="text-xs text-[#1C1C1C]">Out for Delivery</span>
          </div>
          <div className="flex-1 h-0.5 bg-[#E5E7EB] mx-2" />
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-full bg-[#F3F4F6] flex items-center justify-center mb-2">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#9CA3AF"
                strokeWidth="2"
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </div>
            <span className="text-xs text-[#9CA3AF]">Delivered</span>
          </div>
        </div>

        {/* Shipment Details */}
        <div className="px-4 pb-4">
          <div className="border border-gray-200 rounded-lg p-4">
            <h4 className="font-Open font-semibold text-[#3B82F6] mb-3">
              Shipment Details
            </h4>
            <p className="font-Open text-sm text-[#4B5563] mb-3">
              We're happy to inform you that your{" "}
              <span className="font-semibold">[Order ID]</span>was{" "}
              <span className="font-semibold">[Order Status]</span> has been
              successfully confirmed! We will be delivering the order to you in
              3-5 days.
            </p>
            <h5 className="font-Open font-semibold text-[#3B82F6] text-sm mb-1">
              Delivery Address
            </h5>
            <p className="font-Open text-sm text-[#4B5563] mb-4">
              [Delivery Address]
            </p>

            <h4 className="font-Open font-semibold text-[#3B82F6] mb-3">
              Product Information
            </h4>
            <div className="flex justify-between mb-4">
              <div>
                <p className="font-Open text-xs text-[#6B7280]">Product Name</p>
                <p className="font-Open text-sm text-[#1C1C1C]">
                  [Product Name]
                </p>
              </div>
              <div>
                <p className="font-Open text-xs text-[#6B7280]">Quantity</p>
                <p className="font-Open text-sm text-[#1C1C1C]">[Quantity]</p>
              </div>
            </div>

            {/* Tracking Info Box */}
            <div className="bg-[#F9FAFB] rounded-lg p-3 mb-4">
              <p className="font-Open text-sm text-[#4B5563]">
                Your order is being handled by{" "}
                <span className="font-semibold">[Courier Partner]</span>
              </p>
              <p className="font-Open text-sm font-semibold text-[#1C1C1C]">
                [Tracking Number]
              </p>
              <p className="font-Open text-sm text-[#4B5563]">
                You can track your delivery by{" "}
                <span className="text-[#3B82F6] font-semibold">
                  [Movement Tracking Link]
                </span>
              </p>
            </div>

            {/* Track Now Button */}
            <button className="w-full bg-[#3B82F6] text-white rounded-lg py-3 font-semibold hover:bg-[#2563EB] transition-colors">
              Track Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-full flex flex-col bg-white relative">
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200">
        <button
          onClick={onBack}
          className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <BackArrowIcon />
        </button>
        <h2 className="font-Lato font-normal text-xl text-[#323232]">
          Order Tracking Templates
        </h2>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 px-5">
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-6 py-3 font-Open text-sm font-medium transition-colors relative ${
              activeTab === tab
                ? "text-[#3B82F6]"
                : "text-[#6B7280] hover:text-[#1C1C1C]"
            }`}
          >
            {tab}
            {activeTab === tab && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#3B82F6]" />
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto bg-[#F9FAFB]">
        {activeTab === "WhatsApp" && <WhatsAppContent />}
        {activeTab === "SMS" && <SMSContent />}
        {activeTab === "Email" && <EmailContent />}
      </div>

      {/* Fixed Footer */}
      <div 
        className="fixed bottom-0 bg-white border-t border-gray-200 py-3 px-4 z-10"
        style={{ width: "-webkit-fill-available" }}
      >
        <p className="text-center text-xs text-[#6B7280] font-Open">
          This is a preview of how your customers will see the notification
        </p>
      </div>
    </div>
  );
};

const DeltaOnBlaze: React.FunctionComponent<IDeltaOnBlazeProps> = ({
  setOpenRightModalForDelta,
}) => {
  const [communicationChannels, setCommunicationChannels] = useState<any>([]);
  const [filteredRateCard, setFilteredRateCard] = useState<any>([]);
  const [activationHistory, setActivationHistory] = useState<any[]>([]);
  const [isActivationHistoryOpen, setIsActivationHistoryOpen] = useState(false);
  const [isPricingOpen, setIsPricingOpen] = useState(false);
  const [rateCardDetails, setRateCardDetails] = useState({
    rateCardName: "",
    rateCardId: "",
  });

  // State for Order Tracking Templates view
  const [showTemplatesView, setShowTemplatesView] = useState(false);
  const [selectedTemplateName, setSelectedTemplateName] = useState("");

  const ColumnsHelper = createColumnHelper<any>();

  const handleViewClick = (templateName: string, isEnabled: boolean) => {
    if (!isEnabled) return;
    setSelectedTemplateName(templateName);
    setShowTemplatesView(true);
  };

  const columns = [
    ColumnsHelper.accessor("template", {
      header: () => {
        return (
          <div className="">
            <p className="font-Open text-sm font-semibold leading-4  text-[#1C1C1C] self-center whitespace-nowrap">
              Template
            </p>
          </div>
        );
      },

      cell: (info: any) => {
        const TemplateName = info?.row?.original?.templateName;
        const AllAdminCheck = info?.row?.original?.isAdminChecked;
        return (
          <div className="flex gap-x-2">
            <p
              className={`${
                AllAdminCheck
                  ? "font-Open text-sm font-semibold leading-[16px]  text-[#1C1C1C] self-center whitespace-nowrap"
                  : "font-Open text-sm font-semibold leading-[16px]  text-[#E8E8E8] self-center whitespace-nowrap cursor-not-allowed"
              }`}
            >
              {TemplateName || "-"}
            </p>
            <img
              src={AllAdminCheck ? InfoIcon : InfoIconDisabled}
              alt="tooltip-icon"
              className={`${
                AllAdminCheck ? "cursor-pointer" : "cursor-not-allowed"
              }`}
              data-tooltip-id="my-tooltip-delivery-max"
              data-tooltip-content={`${
                TemplateName === "Tracking Updates"
                  ? "Applicable for Picked Up, Reached Destination, Out for Delivery and Delivered status."
                  : TemplateName === "Order Confirmation"
                  ? " Get Order Confirmation from Buyers Before Shipping to Reduce RTO"
                  : TemplateName === "NDR"
                  ? "Get buyer updates on exceptions to confirm cancellations, reschedules, or updated details"
                  : ""
              }`}
            />

            <Tooltip
              id="my-tooltip-delivery-max"
              style={{
                zIndex: 10,
                backgroundColor: "#60D669",
                borderRadius: "6px",
                position: "absolute",
                color: "#FFFFFF",
                width: "270px",
                fontSize: "12px",
                lineHeight: "14px",
                fontFamily: "Open Sans",
                fontWeight: "500",
                letterSpacing: "1px",
              }}
            />
          </div>
        );
      },
    }),

    ColumnsHelper.accessor("whatsApp", {
      header: () => {
        return (
          <div>
            <p className="font-Open text-sm font-semibold leading-4  text-[#1C1C1C] self-center whitespace-nowrap">
              WhatsApp
            </p>
          </div>
        );
      },

      cell: (info: any) => {
        const templateName = info?.row?.original?.templateName;
        const channel = info?.row?.original?.communicationChannels.find(
          (channel: any) => channel?.channelName === "Whatsapp"
        );

        const AllAdminCheck =
          info?.row?.original?.isAdminChecked && channel?.isAdminChecked;

        return (
          <div className="flex justify-center">
            <Checkbox
              checkboxClassName={`${
                AllAdminCheck ? "!cursor-pointer" : " !cursor-not-allowed"
              }`}
              checked={channel?.isChecked}
              onChange={(e) =>
                handleCheckboxChange(templateName, "Whatsapp", e.target.checked)
              }
              disabled={!AllAdminCheck}
              name={"WhatsApp"}
              style={{
                accentColor: "black",
                width: "15px",
                height: "15px",
              }}
            />
          </div>
        );
      },
    }),
    ColumnsHelper.accessor("email", {
      header: () => {
        return (
          <div>
            <p className="font-Open text-sm font-semibold leading-4  text-[#1C1C1C] self-center whitespace-nowrap">
              Email
            </p>
          </div>
        );
      },

      cell: (info: any) => {
        const templateName = info?.row?.original?.templateName;
        const channel = info?.row?.original?.communicationChannels.find(
          (channel: any) => channel?.channelName === "Email"
        );
        const AllAdminCheck =
          info?.row?.original?.isAdminChecked && channel?.isAdminChecked;
        return (
          <div className="flex justify-center">
            <Checkbox
              checkboxClassName={`${
                AllAdminCheck ? "!cursor-pointer" : " !cursor-not-allowed"
              }`}
              checked={channel?.isChecked || false}
              onChange={(e) =>
                handleCheckboxChange(templateName, "Email", e.target.checked)
              }
              disabled={!AllAdminCheck}
              name={"Email"}
              style={{
                accentColor: "black",
                width: "15px",
                height: "15px",
              }}
            />
          </div>
        );
      },
    }),
    ColumnsHelper.accessor("sms", {
      header: () => {
        return (
          <div>
            <p className="font-Open text-sm font-semibold leading-4  text-[#1C1C1C] self-center whitespace-nowrap">
              SMS
            </p>
          </div>
        );
      },

      cell: (info: any) => {
        const templateName = info?.row?.original?.templateName;
        const channel = info?.row?.original?.communicationChannels.find(
          (channel: any) => channel?.channelName === "SMS"
        );
        const AllAdminCheck =
          info?.row?.original?.isAdminChecked && channel?.isAdminChecked;
        return (
          <div className="flex justify-center">
            <Checkbox
              checkboxClassName={`${
                AllAdminCheck ? "!cursor-pointer" : " !cursor-not-allowed"
              }`}
              checked={channel?.isChecked || false}
              onChange={(e) =>
                handleCheckboxChange(templateName, "SMS", e.target.checked)
              }
              disabled={!AllAdminCheck}
              name={"SMS"}
              style={{
                accentColor: "black",
                width: "15px",
                height: "15px",
              }}
            />
          </div>
        );
      },
    }),

    // NEW VIEW COLUMN
    ColumnsHelper.accessor("view", {
      header: () => {
        return (
          <div>
            <p className="font-Open text-sm font-semibold leading-4 text-[#1C1C1C] self-center whitespace-nowrap">
              View
            </p>
          </div>
        );
      },

      cell: (info: any) => {
        const templateName = info?.row?.original?.templateName;
        const AllAdminCheck = info?.row?.original?.isAdminChecked;

        return (
          <div className="flex justify-center">
            <button
              onClick={() => handleViewClick(templateName, AllAdminCheck)}
              className={`p-1 rounded transition-colors ${
                AllAdminCheck
                  ? "cursor-pointer hover:bg-gray-100"
                  : "cursor-not-allowed"
              }`}
              disabled={!AllAdminCheck}
              data-tooltip-id="view-template-tooltip"
              data-tooltip-content="View Templates"
            >
              <EyeIcon disabled={!AllAdminCheck} />
            </button>
            <Tooltip
              id="view-template-tooltip"
              style={{
                zIndex: 10,
                backgroundColor: "#1C1C1C",
                borderRadius: "4px",
                color: "#FFFFFF",
                fontSize: "12px",
                padding: "4px 8px",
              }}
            />
          </div>
        );
      },
    }),

    ColumnsHelper.accessor("selectYourCommunication", {
      header: () => {
        return (
          <div className="">
            <p className="font-Open text-sm font-semibold leading-4  text-[#1C1C1C] self-center whitespace-nowrap">
              Mode
            </p>
          </div>
        );
      },

      cell: (info: any) => {
        const templateName = info?.row?.original?.templateName;
        const paymentType = info?.row?.original?.paymentType;
        const AllAdminCheck = info?.row?.original?.isAdminChecked;
        return (
          <div className="flex justify-center">
            <CustomDropDown
              heading="Payment Mode"
              disabled={!AllAdminCheck ? true : false}
              selectClassName={`${
                AllAdminCheck
                  ? "!cursor-pointer !h-[32px]"
                  : "!cursor-not-allowed !h-[32px]"
              }`}
              onChange={(e) =>
                handleDropdownChange(templateName, e.target.value)
              }
              value={paymentType}
              options={[
                {
                  label: "All Orders",
                  value: "ALL",
                },
                {
                  label: "COD Orders",
                  value: "COD",
                },
                {
                  label: "Prepaid Orders",
                  value: "PREPAID",
                },
              ]}
            />
          </div>
        );
      },
    }),
  ];

  const PricingColumns = [
    ColumnsHelper.accessor("channel", {
      header: () => {
        return (
          <div className="">
            <p className="font-Open text-sm font-semibold leading-4  text-[#1C1C1C] self-center whitespace-nowrap">
              Channel
            </p>
          </div>
        );
      },

      cell: (info: any) => {
        const TemplateName = info?.row?.original?.templateName;
        const AllAdminCheck = info?.row?.original?.isAdminChecked;

        return (
          <div className="flex gap-x-2">
            {AllAdminCheck ? (
              <></>
            ) : (
              <p className="font-Open text-sm font-semibold leading-[16px]  text-[#1C1C1C] self-center whitespace-nowrap">
                {TemplateName || "-"}
              </p>
            )}
          </div>
        );
      },
    }),
    ColumnsHelper.accessor("whatsApp", {
      header: () => {
        return (
          <div>
            <p className="font-Open text-sm font-semibold leading-4  text-[#1C1C1C] self-center whitespace-nowrap">
              WhatsApp
            </p>
          </div>
        );
      },

      cell: (info: any) => {
        const channel = info?.row?.original?.communicationChannels.find(
          (channel: any) => channel?.channelName === "Whatsapp"
        );

        const AllAdminCheck = info?.row?.original?.isAdminChecked;
        return (
          <div className="flex justify-center">
            {AllAdminCheck ? (
              <></>
            ) : (
              <span className="font-Open text-[10px] font-normal leading-4  text-[#1C1C1C]">
                {channel?.price > 0 ? `₹${channel.price}` : "-"}
              </span>
            )}
          </div>
        );
      },
    }),
    ColumnsHelper.accessor("email", {
      header: () => {
        return (
          <div>
            <p className="font-Open text-sm font-semibold leading-4  text-[#1C1C1C] self-center whitespace-nowrap">
              Email
            </p>
          </div>
        );
      },

      cell: (info: any) => {
        const channel = info?.row?.original?.communicationChannels.find(
          (channel: any) => channel?.channelName === "Email"
        );
        return (
          <div className="flex justify-center">
            <span className="font-Open text-[10px] font-normal leading-4  text-[#1C1C1C]">
              {channel?.price > 0 ? `₹${channel.price}` : "-"}
            </span>
          </div>
        );
      },
    }),
    ColumnsHelper.accessor("sms", {
      header: () => {
        return (
          <div>
            <p className="font-Open text-sm font-semibold leading-4  text-[#1C1C1C] self-center whitespace-nowrap">
              SMS
            </p>
          </div>
        );
      },

      cell: (info: any) => {
        const channel = info?.row?.original?.communicationChannels.find(
          (channel: any) => channel?.channelName === "SMS"
        );
        return (
          <div className="flex justify-center">
            <span className="font-Open text-[10px] font-normal leading-4  text-[#1C1C1C]">
              {channel?.price > 0 ? `₹${channel.price}` : "-"}
            </span>
          </div>
        );
      },
    }),
  ];

  // Activation History columns for Shipment Status section
  const ActivationHistoryColumns = [
    ColumnsHelper.accessor("date", {
      header: () => (
        <p className="font-Open text-sm font-semibold leading-4 text-[#1C1C1C]">
          Date
        </p>
      ),
      cell: (info: any) => (
        <p className="font-Open text-sm font-normal leading-4 text-[#1C1C1C]">
          {info?.row?.original?.date}
        </p>
      ),
    }),
    ColumnsHelper.accessor("action", {
      header: () => (
        <p className="font-Open text-sm font-semibold leading-4 text-[#1C1C1C]">
          Action
        </p>
      ),
      cell: (info: any) => {
        const action = info?.row?.original?.action;
        return (
          <span
            className={`px-3 py-1 rounded-md text-sm font-medium ${
              action === "Enabled"
                ? "bg-[#E6F9E8] text-[#27B031]"
                : "bg-[#FFEBE8] text-[#F05D48]"
            }`}
          >
            {action}
          </span>
        );
      },
    }),
    ColumnsHelper.accessor("templateName", {
      header: () => (
        <p className="font-Open text-sm font-semibold leading-4 text-[#1C1C1C]">
          Template
        </p>
      ),
      cell: (info: any) => (
        <p className="font-Open text-sm font-normal leading-4 text-[#1C1C1C]">
          {info?.row?.original?.templateName}
        </p>
      ),
    }),
    ColumnsHelper.accessor("mode", {
      header: () => (
        <p className="font-Open text-sm font-semibold leading-4 text-[#1C1C1C]">
          Mode
        </p>
      ),
      cell: (info: any) => (
        <p className="font-Open text-sm font-normal leading-4 text-[#1C1C1C]">
          {info?.row?.original?.mode}
        </p>
      ),
    }),
  ];

  // Generate activation history data based on current state
  const getActivationHistoryData = () => {
    return activationHistory.map((item: any) => ({
      date: new Date(item.updatedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) + " " + new Date(item.updatedAt).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true }),
      action: item.action === "DISABLED" ? "Disabled" : "Enabled",
      templateName: item.templateName,
      mode: item.channelName,
    }));
  };

  const handleCheckboxChange = (
    templateName: string,
    channelName: string,
    checked: boolean
  ) => {
    setCommunicationChannels((prevChannels: any) => {
      return prevChannels.map((template: any) => {
        if (template.templateName === templateName) {
          const updatedChannels = template.communicationChannels.map(
            (channel: any) => {
              if (channel.channelName === channelName) {
                return { ...channel, isChecked: checked };
              }
              return channel;
            }
          );

          // Determine if the outer isChecked should be true
          const isTemplateChecked = updatedChannels.some(
            (channel: any) => channel.isChecked
          );

          return {
            ...template,
            communicationChannels: updatedChannels,
            isChecked: isTemplateChecked,
          };
        }
        return template;
      });
    });
  };

  const handleDropdownChange = (templateName: string, value: string) => {
    setCommunicationChannels((prevChannels: any) => {
      return prevChannels.map((template: any) => {
        if (template.templateName === templateName) {
          return { ...template, paymentType: value };
        }
        return template;
      });
    });
  };

  const handleSave = async () => {
    const payload = {
      rateCardId: rateCardDetails?.rateCardId,
      rateCardName: rateCardDetails?.rateCardName,
      rates: communicationChannels,
    };

    try {
      const { data: response } = await POST(
        UPDATE_COMMUNICATION_RATE_CARD,
        payload
      );

      if (response?.success) {
        toast.success(response?.message);
        setOpenRightModalForDelta(false);
      } else {
        toast.error(response?.message);
      }
    } catch (error) {
      toast.error("Failed to update communication rate card");
      return error;
    }
  };

  useEffect(() => {
    const fetchCommunicationRateCard = async () => {
      try {
        const response = await POST(GET_COMMUNICATION_RATE_CARD, {});
        if (response?.data?.success) {
          setRateCardDetails({
            rateCardId: response?.data?.data[0]?.rateCardId || "",
            rateCardName: response?.data?.data[0]?.rateCardName || "",
          });

          const rateCardData = response.data?.data?.[0]?.rates.filter(
            (rate: any) => rate.templateName !== "Global Rate"
          );
          setCommunicationChannels(rateCardData);

          const filteredData = rateCardData
            .filter((template: any) => template.isAdminChecked === true)
            .map((template: any) => ({
              templateName: template.templateName,
              communicationChannels: template.communicationChannels.filter(
                (channel: any) => channel.isAdminChecked === true
              ),
            }));
          setFilteredRateCard(filteredData);
          setActivationHistory(response?.data?.activationHistory || []);
        } else {
          toast.error("Failed to fetch communication rate card");
        }
      } catch (error) {
        console.error("Error fetching communication rate card:", error);
      }
    };

    fetchCommunicationRateCard();
  }, []);

  // If showing templates view, render that instead
  if (showTemplatesView) {
    return (
      <OrderTrackingTemplates
        templateName={selectedTemplateName}
        onBack={() => setShowTemplatesView(false)}
      />
    );
  }

  return (
    <>
      <div>
        <div className="flex justify-between px-5 py-4">
          <p className="font-Lato font-normal text-2xl text-[#323232] leading-8">
            Delivery Max{" "}
            <span className="font-Lato font-normal text-md text-lg text-[#323232] leading-[22px]">
              {`( Send live updates about your order straight to buyers on multiple
              channels! )`}
            </span>
          </p>
          <img
            src={CloseIcon}
            alt="close-icon"
            onClick={() => setOpenRightModalForDelta(false)}
            className="cursor-pointer"
          />
        </div>

        <div className="h-[calc(100vh-90px)] overflow-y-scroll pb-2">
          <div>
            <p className="font-Open font-semibold text-md  text-[#1C1C1C] leading-[22px] px-5 pt-2">
              Setup Your Communication{" "}
              <span>
                {`(${rateCardDetails?.rateCardName} - ${rateCardDetails?.rateCardId})`}
              </span>
            </p>
            <div className="p-2">
              <CustomTable
                columnsData={columns}
                rowData={communicationChannels || []}
              />
            </div>
          </div>
          <div className="bg-[#F9FAFB] rounded-lg shadow-sm mx-2 mt-5">
            <div className="px-5 py-2 text-xs flex font-Open leading-5 bg-[#F2F4F5] mx-2 mt-2 gap-1 rounded-md">
              <b>Note:</b>&nbsp;
              <div className="items-center text-justify">
                For API or channel orders, the <b>&nbsp;Brand Name&nbsp;</b>{" "}
                shared via API will take priority over the Brand Name saved in
                the <b>&nbsp;My Profile&nbsp;</b> section.
              </div>
            </div>
            <div
              className="flex items-center gap-2 px-5 py-3 cursor-pointer select-none"
              onClick={() => setIsPricingOpen(!isPricingOpen)}
            >
              <svg
                className={`w-4 h-4 text-[#1C1C1C] transition-transform duration-200 ${
                  isPricingOpen ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
              <p className="font-Open font-semibold text-lg text-[#1C1C1C] leading-[22px]">
                Pricing
              </p>
            </div>
            {isPricingOpen && (
              <div className="p-2 relative ">
                <CustomTable
                  columnsData={PricingColumns}
                  rowData={filteredRateCard || []}
                  minHeight="240px"
                  rowHeight={50}
                />
              </div>
            )}
          </div>

          {/* Shipment Status Section */}
          <div className="mx-2 mt-5 mb-20 bg-[#F9FAFB] rounded-lg shadow-sm relative ">
            <p className="font-Open font-semibold text-lg text-[#155DFC] leading-[22px] px-4 pt-4">
              Shipment Status
            </p>
            <div
              className="flex items-center gap-2 px-4 py-3 cursor-pointer select-none"
              onClick={() =>
                setIsActivationHistoryOpen(!isActivationHistoryOpen)
              }
            >
              <svg
                className={`w-4 h-4 text-[#1C1C1C] transition-transform duration-200 ${
                  isActivationHistoryOpen ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
              <p className="font-Open font-medium text-sm text-[#1C1C1C] leading-[20px]">
                Activation History
              </p>
            </div>
            {isActivationHistoryOpen && (
              <div className="px-2 pb-4">
                <CustomTable
                  columnsData={ActivationHistoryColumns}
                  rowData={getActivationHistoryData()}
                  minHeight="240px"
                  rowHeight={50}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        className="flex justify-end gap-x-5 shadow-lg border-[1px] h-[68px] bg-[#FFFFFF] px-6 py-4 rounded-tr-[32px] rounded-tl-[32px] fixed bottom-0"
        style={{ width: "-webkit-fill-available" }}
      >
        <ServiceButton
          text={"UPDATE SETTINGS"}
          onClick={handleSave}
          className="!bg-[#60D669] !border-[#60D669] h-[36px] !text-[#FFFFFF] !px-4 !py-2 !font-Open !font-semibold !text-[14px] !leading-5 !rounded-[4px] hover:!bg-[#27B031] hover:!shadow-cardShadow2a focus:!bg-[#60D669] focus:border focus:!border-[#27B031]"
        />
      </div>
    </>
  );
};

export default DeltaOnBlaze;

